import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Grid,
  Autocomplete,
  IconButton,
  Typography,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { Article } from '../types';
import { Client } from '../types/client';
import {
  StockReturnNewCreate,
  StockReturnNew,
  StockReturnItemCreate,
} from '../types/stockReturnNew';
import { stockReturnsAPI, auditLogsAPI } from '../services/api';
import { extractErrorMessage } from '../utils/errorHandler';
import usePdfPreview from '../hooks/usePdfPreview';
// generateStockReturnPDF is dynamically imported in handlePrint so the jspdf
// chunk only loads when the user clicks Print.
import useAlert from '../hooks/useAlert';
import useFormDraft from '../hooks/useFormDraft';
import AlertDialog from './AlertDialog';
import ConfirmationDialog, { ConfirmationField } from './ConfirmationDialog';
import { SlideUpTransition, useMinimizable, DialogControls, MinimizedBar } from './MinimizableDialog';

interface StockReturnAddProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  articles: Article[];
  clients: Client[];
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

interface ArticleLineItem {
  article_id: number;
  article?: Article;
  quantity: number | null | string;
  unit_price: number | null | string;
}

const StockReturnAdd: React.FC<StockReturnAddProps> = ({
  open,
  onClose,
  onSuccess,
  articles,
  clients,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );

  const { alertState, showError, showSuccess, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    bon_retour_number: '',
    client_id: null as number | null,
  });

  const [articleLines, setArticleLines] = useState<ArticleLineItem[]>([
    {
      article_id: 0,
      quantity: null,
      unit_price: null,
    },
  ]);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdResponse, setCreatedResponse] = useState<StockReturnNew | null>(null);

  // Cache the in-progress form so an accidental backdrop-close doesn't wipe it.
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: typeof formData;
    articleLines: ArticleLineItem[];
  }>('stock-return');

  // Restore a cached draft when the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      const draft = loadDraft();
      if (draft) {
        setFormData(draft.formData);
        setArticleLines(draft.articleLines);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-generate unique BON RETOUR number when dialog opens
  useEffect(() => {
    const generateBonRetourNumber = async () => {
      if (open) {
        try {
          // Use backend endpoint to get next BON RETOUR number (includes deleted records)
          const response = await stockReturnsAPI.getNextBonRetourNumber();
          setFormData((prev) => ({
            ...prev,
            bon_retour_number: response.bon_retour_number,
          }));
        } catch (error) {
          console.error('Error generating BON RETOUR number:', error);
          // Fallback to timestamp-based BON RETOUR number
          const timestamp = Date.now();
          const bonRetourNumber = `BR-${timestamp}`;
          setFormData((prev) => ({
            ...prev,
            bon_retour_number: bonRetourNumber,
          }));
        }
      }
    };

    generateBonRetourNumber();
  }, [open]);

  const formatNumber = (value: any, decimals: number = 2): string => {
    return Number(value || 0).toFixed(decimals);
  };

  const handleAddArticleLine = () => {
    setArticleLines([
      ...articleLines,
      {
        article_id: 0,
        quantity: null,
        unit_price: null,
      },
    ]);
  };

  const handleRemoveArticleLine = (index: number) => {
    if (articleLines.length > 1) {
      setArticleLines(articleLines.filter((_, i) => i !== index));
    }
  };

  const handleArticleChange = (index: number, article: Article | null) => {
    const newLines = [...articleLines];

    if (article) {
      newLines[index] = {
        ...newLines[index],
        article_id: article.id,
        article: article,
      };
    } else {
      newLines[index] = {
        article_id: 0,
        quantity: null,
        unit_price: null,
      };
    }

    setArticleLines(newLines);
  };

  const handleArticleFieldChange = (
    index: number,
    field: keyof ArticleLineItem,
    value: any
  ) => {
    const newLines = [...articleLines];
    newLines[index] = {
      ...newLines[index],
      [field]: value,
    };
    setArticleLines(newLines);
  };

  const calculateGrandTotal = (): number => {
    return articleLines.reduce((total, line) => {
      const quantity = typeof line.quantity === 'string'
        ? parseFloat(line.quantity.replace(',', '.')) || 0
        : (line.quantity ?? 0);
      const unitPrice = typeof line.unit_price === 'string'
        ? parseFloat(line.unit_price.replace(',', '.')) || 0
        : (line.unit_price ?? 0);
      const lineTotal = quantity * unitPrice;
      return total + lineTotal;
    }, 0);
  };

  const handleSubmitClick = () => {
    if (!formData.bon_retour_number || !formData.bon_retour_number.trim()) {
      showError('Veuillez saisir le numéro de BON RETOUR');
      return;
    }

    if (!formData.client_id) {
      showError('Veuillez sélectionner un client');
      return;
    }

    for (let i = 0; i < articleLines.length; i++) {
      const line = articleLines[i];

      if (!line.article_id || line.article_id === 0) {
        showError(`Veuillez sélectionner un article pour la ligne ${i + 1}`);
        return;
      }

      const quantity = typeof line.quantity === 'string'
        ? parseFloat(line.quantity.replace(',', '.'))
        : line.quantity;

      if (quantity === null || isNaN(quantity as number) || (quantity as number) <= 0) {
        showError(`La quantité doit être supérieure à 0 pour la ligne ${i + 1}`);
        return;
      }

      const unitPrice = typeof line.unit_price === 'string'
        ? parseFloat(line.unit_price.replace(',', '.'))
        : line.unit_price;

      if (unitPrice === null || isNaN(unitPrice as number) || (unitPrice as number) <= 0) {
        showError(`Le prix unitaire doit être supérieur à 0 pour la ligne ${i + 1}`);
        return;
      }
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Build items array for the new API format
      const items: StockReturnItemCreate[] = articleLines.map((line) => {
        const quantity = typeof line.quantity === 'string'
          ? parseFloat(line.quantity.replace(',', '.'))
          : (line.quantity ?? 0);
        const unitPrice = typeof line.unit_price === 'string'
          ? parseFloat(line.unit_price.replace(',', '.'))
          : (line.unit_price ?? 0);
        return {
          article_id: line.article_id,
          quantity: Math.round(quantity * 100) / 100,
          unit_price: Math.round(unitPrice * 100) / 100,
        };
      });

      const createData: StockReturnNewCreate = {
        bon_retour_number: formData.bon_retour_number,
        date: formData.date || new Date().toISOString().split('T')[0],
        client_id: formData.client_id!,
        items: items,
      };

      const result = await stockReturnsAPI.create(createData);
      setCreatedResponse(result);
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);
      showSuccess(
        `Retour de stock créé avec succès! ${result.total_articles || result.items?.length || 0} article(s), Total: ${formatNumber(result.grand_total)} MAD`
      );
      clearDraft();
      onSuccess();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrint = () => {
    // Preview the entire BON RETOUR with all articles before print/download.
    if (createdResponse && createdResponse.bon_retour_number) {
      const bonRetourNumber = createdResponse.bon_retour_number;
      openPreview({
        getBlob: async () => {
          const { generateStockReturnPDF } = await import('../utils/pdfGenerator');
          return (await generateStockReturnPDF(bonRetourNumber, true)) as Blob;
        },
        fileName: `${bonRetourNumber}.pdf`,
      });
      auditLogsAPI.logPdfDownload('StockReturn', createdResponse.id || 0, `Aperçu PDF - ${bonRetourNumber}`);
    }
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    handleClose();
  };

  const getConfirmationFields = (): ConfirmationField[] => {
    const fields: ConfirmationField[] = [];

    fields.push({ label: 'Date', value: formData.date });
    fields.push({ label: 'BON RETOUR N°', value: formData.bon_retour_number });

    if (formData.client_id) {
      const client = clients.find((c) => c.id === formData.client_id);
      if (client) {
        fields.push({ label: 'Client', value: `${client.code} - ${client.name}` });
      }
    }

    fields.push({ label: '---', value: '---' });
    fields.push({ label: 'Articles', value: `${articleLines.length}`, bold: true });

    articleLines.forEach((line, index) => {
      const article = articles.find((a) => a.id === line.article_id);
      if (article) {
        const quantity = typeof line.quantity === 'string'
          ? parseFloat(line.quantity.replace(',', '.')) || 0
          : (line.quantity ?? 0);
        const unitPrice = typeof line.unit_price === 'string'
          ? parseFloat(line.unit_price.replace(',', '.')) || 0
          : (line.unit_price ?? 0);

        fields.push({
          label: `Article ${index + 1}`,
          value: `${article.reference} - ${article.designation}`,
        });
        fields.push({
          label: `  Quantité`,
          value: `${quantity}`,
        });
        fields.push({
          label: `  Prix Unitaire`,
          value: `${formatNumber(unitPrice)} MAD`,
        });
        fields.push({
          label: `  Total`,
          value: `${formatNumber(quantity * unitPrice)} MAD`,
        });
      }
    });

    fields.push({ label: '---', value: '---' });
    fields.push({
      label: 'TOTAL GÉNÉRAL',
      value: `${formatNumber(calculateGrandTotal())} MAD`,
      bold: true,
    });

    return fields;
  };

  // Annuler = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    setFormData({
      date: new Date().toISOString().split('T')[0],
      bon_retour_number: '',
      client_id: null,
    });
    setArticleLines([
      {
        article_id: 0,
        quantity: null,
        unit_price: null,
      },
    ]);
    setShowConfirmDialog(false);
    setIsSubmitting(false);
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      saveDraft({ formData, articleLines });
      onClose();
    }
  };

  return (
    <>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="lg" fullWidth TransitionComponent={SlideUpTransition}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Nouveau retour de stock (Multi-articles)
          <DialogControls color="#2196F3" onMinimize={minimize} onClose={handleClose} />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="BON RETOUR N°"
                  value={formData.bon_retour_number}
                  fullWidth
                  required
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="Généré automatiquement"
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={clients}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  value={clients.find((c) => c.id === formData.client_id) || null}
                  onChange={(_, newValue) =>
                    setFormData({ ...formData, client_id: newValue?.id || null })
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Client" required />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Articles retournés</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddArticleLine}
                variant="outlined"
                size="small"
              >
                Ajouter un article
              </Button>
            </Box>

            {articleLines.map((line, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <Autocomplete
                      options={articles}
                      getOptionLabel={(option) => `${option.reference} - ${option.designation}`}
                      value={articles.find((a) => a.id === line.article_id) || null}
                      onChange={(_, newValue) => handleArticleChange(index, newValue)}
                      renderInput={(params) => (
                        <TextField {...params} label="Article" required size="small" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Quantité"
                      type="text"
                      value={line.quantity ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'quantity', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'quantity', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'quantity', numValue);
                          } else {
                            handleArticleFieldChange(index, 'quantity', null);
                          }
                        }
                      }}
                      fullWidth
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Prix Unit."
                      type="text"
                      value={line.unit_price ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'unit_price', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'unit_price', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'unit_price', numValue);
                          } else {
                            handleArticleFieldChange(index, 'unit_price', null);
                          }
                        }
                      }}
                      fullWidth
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Total"
                      value={(() => {
                        const quantity = typeof line.quantity === 'string'
                          ? parseFloat(line.quantity.replace(',', '.')) || 0
                          : (line.quantity ?? 0);
                        const unitPrice = typeof line.unit_price === 'string'
                          ? parseFloat(line.unit_price.replace(',', '.')) || 0
                          : (line.unit_price ?? 0);
                        return formatNumber(quantity * unitPrice);
                      })()}
                      fullWidth
                      size="small"
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveArticleLine(index)}
                      disabled={articleLines.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}

            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="h6" align="right">
                Total Général: {formatNumber(calculateGrandTotal())} MAD
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSubmitClick} variant="contained" color="primary">
            Créer le retour
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={open && minimized}
        color="#2196F3"
        title="Nouveau retour de stock (Multi-articles)"
        onRestore={restore}
        onClose={handleClose}
      />

      <ConfirmationDialog
        open={showConfirmDialog}
        title="Confirmer le retour de stock"
        message="Veuillez vérifier les informations avant de créer le retour:"
        fields={getConfirmationFields()}
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowConfirmDialog(false)}
        confirmText="Confirmer"
        cancelText="Retour"
        loading={isSubmitting}
      />

      {/* Success Dialog with Print Option */}
      <Dialog
        open={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#FF9800', color: 'white' }}>
          Retour créé avec succès!
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            Le retour de stock a été créé avec succès.
          </Typography>
          <Typography sx={{ mt: 1 }}>
            Voulez-vous imprimer le BON DE RETOUR?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessDialogClose}>Fermer</Button>
          <Button
            onClick={async () => {
              await handlePrint();
              handleSuccessDialogClose();
            }}
            variant="contained"
            startIcon={<PrintIcon />}
            sx={{
              backgroundColor: '#FF9800',
              '&:hover': {
                backgroundColor: '#F57C00',
              },
            }}
          >
            Imprimer
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </>
  );
};

export default StockReturnAdd;
